"use client";

import { useEffect, useState, type DragEvent } from "react";
import {
    Upload,
    FileSpreadsheet,
    Loader2,
    Download,
    CheckCircle,
    AlertTriangle,
    XCircle,
    AlertCircle,
    Info
} from "lucide-react";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import { AxiosError } from "axios";
import PackagePriceList from "./PackagePriceList"; // Pastikan path ini benar
import { Separator } from "@/components/ui/separator";
import ClothingSizeModal from "@/components/ClothingSizeModal";
import { ITEMS_BLOOD_TYPE, ITEMS_CLOTHES_SIZE, ITEMS_GENDER } from "@/constants/options";

// ✅ 1. EXPORT Interface agar bisa dipakai di component lain
export interface SkippedItem {
    email: string;
    name?: string;
    reason: string;
}

export interface ImportResponse {
    message: string;
    imported_count: number;
    skipped_count: number;
    imported: Record<string, unknown>[]; // Type safe object
    skipped: SkippedItem[];
}

interface ImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    // ✅ 2. FIX: Wajib menerima parameter data hasil import
    onImported: (data: ImportResponse) => void;
}

export default function ImportModal({
    isOpen,
    onClose,
    onImported,
}: ImportModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState<number>(0);
    const [skippedData, setSkippedData] = useState<SkippedItem[]>([]);
    const [importedData, setImportedData] = useState<Record<string, any>[]>([]);

    const [isDragging, setIsDragging] = useState(false);

    const { data: activePeriod, isLoading, isError } = useActivePeriod();

    /** Reset state setiap kali modal dibuka */
    useEffect(() => {
        if (isOpen) {
            setFile(null);
            setProgress(0);
            setImporting(false);
            setSkippedData([]);
            setImportedData([]);
        }
    }, [isOpen]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFile(e.target.files?.[0] || null);
        setSkippedData([]);
        setImportedData([]);
    };

    // === HANDLER DRAG AND DROP (BARU) ===

    // 1. Saat file diseret di atas area (Wajib preventDefault agar bisa di-drop)
    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!importing) {
            setIsDragging(true);
        }
    };

    // 2. Saat file keluar dari area
    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    // 3. Saat file dilepas (DROP)
    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        if (importing) return;

        // Ambil file dari dataTransfer
        const droppedFiles = e.dataTransfer.files;

        if (droppedFiles && droppedFiles.length > 0) {
            const droppedFile = droppedFiles[0];

            // Validasi ekstensi sederhana (opsional, tapi disarankan)
            const fileType = droppedFile.name.split('.').pop()?.toLowerCase();
            if (fileType === 'xlsx' || fileType === 'csv') {
                setFile(droppedFile);
                setSkippedData([]); // Reset error lama
                toast.success("File berhasil dipilih via Drag & Drop");
            } else {
                toast.error("Format file harus .xlsx atau .csv");
            }
        }
    };
    // ======================================

    const handleDownloadTemplate = (): void => {
        window.open("/templates/template_import_peserta_by_pic.xlsx", "_blank");
    };

    const handleExportResults = () => {
        let csvContent = "Status,Nama Peserta,Email,Alasan\n";
        skippedData.forEach((item) => {
            csvContent += `Dilewati/Gagal,"${item.name || '-'}","${item.email}","${item.reason}"\n`;
        });
        importedData.forEach((item) => {
            csvContent += `Berhasil,"${item.full_name || '-'}","${item.email || '-'}","-"\n`;
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Hasil_Import_Peserta_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImport = async (): Promise<void> => {
        if (!file) {
            toast.error("Silakan pilih file terlebih dahulu.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            setImporting(true);
            setProgress(0);
            setSkippedData([]);
            setImportedData([]);
            const toastId = toast.loading("Mengimpor data...");

            // ✅ Panggil API dengan Generic Type ImportResponse
            const { data } = await axiosClient.post<ImportResponse>(
                "/pic/participants/import",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (evt) => {
                        if (evt.total) {
                            const percent = Math.round((evt.loaded * 100) / evt.total);
                            setProgress(percent);
                        }
                    },
                }
            );

            toast.dismiss(toastId);

            // ✅ 3. PENTING: Kirim data ke Parent Component
            onImported(data);

            // Logika UI Lokal (Toast & State Lokal)
            if (data.skipped_count === 0) {
                // Sukses Sempurna
                toast.success(`Import berhasil sepenuhnya!`);
                setImportedData(data.imported || []);
                // setTimeout(() => onClose(), 800); // Jangan tutup agar bisa lihat/export data
            } else if (data.imported_count > 0 && data.skipped_count > 0) {
                // Sukses Sebagian
                toast.success(`Import selesai dengan catatan.`);
                setSkippedData(data.skipped || []);
                setImportedData(data.imported || []);
            } else {
                // Gagal Semua
                toast.error(`Import selesai. Semua data dilewati.`);
                setSkippedData(data.skipped || []);
            }

        } catch (err) {
            toast.dismiss();
            const error = err as AxiosError<{ message?: string; error?: string }>;
            const apiMsg =
                error.response?.data?.message ||
                error.response?.data?.error ||
                "Gagal mengimpor data peserta.";
            toast.error(
                apiMsg.length > 120 ? `${apiMsg.substring(0, 120)}...` : apiMsg,
                { duration: 6000 }
            );
            console.error("Import error:", error.response?.data);
        } finally {
            setImporting(false);
            setProgress(0);
        }
    };

    const [isClothingSizeOpen, setIsClothingSizeOpen] = useState(false);

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open && !importing) onClose();
            }}
        >
            <DialogContent onInteractOutside={(e) => e.preventDefault()} className="lg:max-w-7xl w-full bg-white dark:bg-gray-900 border-0 rounded-2xl shadow-xl max-h-[95vh] overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                        <Upload className="w-6 h-6 text-blue-600" /> Import Peserta Kolektif
                    </DialogTitle>
                    <DialogDescription className="text-gray-500">
                        {isLoading ? (
                            <span className="flex items-center gap-2 text-blue-500">
                                <Loader2 className="w-4 h-4 animate-spin" /> Memuat periode aktif...
                            </span>
                        ) : isError ? (
                            <span className="flex items-center gap-2 text-red-500">
                                <AlertTriangle className="w-4 h-4" /> Gagal memuat periode aktif.
                            </span>
                        ) : (
                            <>
                                Periode aktif:{" "}
                                <b className="text-indigo-600">{activePeriod?.name || "-"}</b>
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <Separator className="mb-1" />

                <div className="mt-6 mb-4 grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-6">
                    {/* Kanan: Daftar Paket */}
                    <div className="flex flex-col gap-6 order-2 lg:order-1">
                        <div className="bg-blue-50 dark:bg-gray-800 rounded-xl border border-blue-100 dark:border-gray-700 p-5 flex flex-col justify-between">
                            <div>
                                <h4 className="text-base font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                    <FileSpreadsheet className="w-5 h-5" />
                                    Daftar Paket
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                                    Untuk melakukan import data, pastikan <b>ID Paket</b> (kolom <b>package_id</b>) sesuai dengan ID yang tersedia berikut:
                                </p>
                            </div>
                        </div>
                        {/* Pastikan component PackagePriceList ada dan diimport dengan benar */}
                        <PackagePriceList compact />

                        {/* Informasi Opsi Valid (Gender, Blood, Size) */}
                        <div className="bg-blue-50 dark:bg-gray-800 rounded-xl border border-blue-100 dark:border-gray-700 p-4 space-y-3">
                            <h4 className="text-base font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                <Info className="w-5 h-5" />
                                Informasi Opsi Kolom
                            </h4>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                                Untuk melakukan import data, pastikan <b>Mengisi Opsi</b> sesuai dengan pilihan yang tersedia berikut:
                            </p>
                            <div className="grid grid-cols-1 gap-3">
                                {/* Gender */}
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-bold mb-1">Gender</p>
                                    <div className="flex flex-wrap gap-1">
                                        {ITEMS_GENDER.map((item) => (
                                            <span key={item.value} className="px-2 py-0.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-[11px] text-gray-700 dark:text-gray-300">
                                                {item.value}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Blood Type */}
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-bold mb-1">Golongan Darah</p>
                                    <div className="flex flex-wrap gap-1">
                                        {ITEMS_BLOOD_TYPE.map((item) => (
                                            <span key={item.value} className="px-2 py-0.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-[11px] text-gray-700 dark:text-gray-300">
                                                {item.value}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Clothes Size */}
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-bold mb-1">Ukuran Baju</p>
                                    <div className="flex flex-wrap gap-1">
                                        {ITEMS_CLOTHES_SIZE.map((item) => (
                                            <span key={item.value} className="px-2 py-0.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-[11px] text-gray-700 dark:text-gray-300">
                                                {item.value}
                                            </span>
                                        ))}
                                    </div>
                                    {/* Tombol Lihat Ukuran Baju tetap di sini */}
                                    <button
                                        type="button"
                                        onClick={() => setIsClothingSizeOpen(true)}
                                        className="text-xs md:text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1.5 mt-3 transition-colors font-medium underline underline-offset-2">
                                        <Info size={14} />
                                        Lihat Ukuran Baju
                                    </button>
                                    <ClothingSizeModal
                                        isOpen={isClothingSizeOpen}
                                        onOpenChange={setIsClothingSizeOpen}
                                    />
                                </div>
                            </div>
                            <p className="text-[10px] text-amber-600 dark:text-amber-400 italic">
                                *Gunakan nilai (value) di atas pada kolom yang sesuai (case-sensitive).
                            </p>
                        </div>
                    </div>

                    {/* Kiri: Template + Upload */}
                    <div className="flex flex-col gap-6 order-1 lg:order-2">
                        {/* Template Card */}
                        <div className="bg-blue-50 dark:bg-gray-800 rounded-xl border border-blue-100 dark:border-gray-700 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                            <div>
                                <h4 className="text-base font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                    <Download className="w-5 h-5" /> Template Excel
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 max-w-md">
                                    Gunakan template ini. Jangan ubah header kolom.
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 max-w-md">
                                    Jika templates tidak sesuai, sistem tidak akan dapat mengimpor data.
                                </p>
                            </div>

                            <Button
                                onClick={handleDownloadTemplate}
                                disabled={importing}
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white shrink-0"
                            >
                                <Download className="w-4 h-4 mr-2" /> Download
                            </Button>
                        </div>

                        {/* --- START: TAMBAHAN PANDUAN IMPORT --- */}
                        <div className="mt-0 p-4 border border-blue-100 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-900/30 rounded-xl">
                            <div className="flex items-start gap-3">
                                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    <h4 className="text-base font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                        Panduan Import
                                    </h4>
                                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-1 leading-relaxed">
                                        Pastikan format data sesuai dengan instruksi teknis untuk menghindari kegagalan sistem.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => window.open("/path/to/panduan_import.pdf", "_blank")}
                                        className="mt-2 flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-800 underline transition-colors"
                                    >
                                        <Download size={14} />
                                        Download Panduan (PDF)
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Upload Card */}
                        <div
                            // Event Handlers dipasang di sini
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            className={`border-2 border-dashed rounded-2xl p-6 transition-all duration-300 flex flex-col justify-between 
                            ${importing
                                    ? "border-gray-300 bg-gray-50 dark:bg-gray-800/60 cursor-not-allowed"
                                    : isDragging // Ubah style jika sedang di-drag
                                        ? "border-blue-500 bg-blue-100/50 dark:bg-blue-900/40 scale-[1.02] shadow-xl"
                                        : "border-blue-300 dark:border-blue-700 bg-blue-50/30 dark:bg-gray-800/40 hover:bg-blue-50 dark:hover:bg-gray-800/60"
                                }`}
                        >
                            <label
                                htmlFor="file-upload"
                                className={`flex flex-col items-center justify-center gap-4 py-24 px-4 text-center cursor-pointer transition-all duration-300 rounded-xl ${file
                                    ? "bg-green-50/50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                                    : ""
                                    } ${importing ? "pointer-events-none opacity-70" : ""}`}
                            >
                                {file ? (
                                    <>
                                        <CheckCircle className="w-12 h-12 text-green-600 animate-pulse" />
                                        <div>
                                            <p className="text-sm text-green-700 dark:text-green-400 font-bold truncate max-w-[250px]">
                                                {file.name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {(file.size / 1024).toFixed(1)} KB • Siap diimpor
                                            </p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="p-4 rounded-full bg-blue-100 dark:bg-blue-900/30">
                                            <Upload className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                                        </div>
                                        <div>
                                            <p className="text-gray-700 dark:text-gray-300 font-medium">
                                                Klik untuk memilih file Excel (.xlsx)
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Pastikan format sesuai template
                                            </p>
                                        </div>
                                    </>
                                )}
                                <input
                                    id="file-upload"
                                    type="file"
                                    accept=".xlsx,.csv"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    disabled={importing}
                                />
                            </label>

                            {/* Progress bar */}
                            {importing && (
                                <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-6 overflow-hidden">
                                    <div
                                        className="bg-blue-600 h-2 transition-all duration-300 ease-out"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            )}

                            <Button
                                onClick={handleImport}
                                disabled={!file || importing}
                                className={`mt-6 w-full py-2 text-white transition-all duration-300 shadow-sm ${importing
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-md"
                                    }`}
                            >
                                {importing ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        Memproses {progress}%...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" /> Mulai Import
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* List Error Lokal (Opsional, karena di parent juga ditampilkan) */}
                        {skippedData.length > 0 && (
                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-4">
                                    <div className="flex items-center gap-2 mb-3 text-red-700 dark:text-red-400">
                                        <AlertCircle className="w-5 h-5" />
                                        <h5 className="font-bold text-sm">
                                            {skippedData.length} Data Gagal Diimpor
                                        </h5>
                                    </div>
                                    <div className="max-h-[150px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-red-200 dark:scrollbar-thumb-red-800">
                                        <ul className="space-y-2">
                                            {skippedData.map((item, idx) => (
                                                <li
                                                    key={idx}
                                                    className="text-xs bg-white dark:bg-gray-800 p-3 rounded-lg border border-red-100 dark:border-red-900/50 shadow-sm"
                                                >
                                                    <div className="flex items-start gap-2">
                                                        <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                                        <div>
                                                            <span className="font-bold text-gray-800 dark:text-gray-200 block mb-0.5">
                                                                {item.name ? `${item.name} (${item.email})` : item.email}
                                                            </span>
                                                            <span className="text-red-600 dark:text-red-400">
                                                                {item.reason}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {importedData.length > 0 && (
                        <div className="mt-4 px-6 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800 p-4">
                                <div className="flex items-center gap-2 mb-3 text-emerald-700 dark:text-emerald-400">
                                    <CheckCircle className="w-5 h-5" />
                                    <h5 className="font-bold text-sm">
                                        {importedData.length} Data Berhasil Diimpor
                                    </h5>
                                </div>
                                <div className="max-h-[120px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-emerald-200 dark:scrollbar-thumb-emerald-800">
                                    <ul className="space-y-2">
                                        {importedData.map((item, idx) => (
                                            <li
                                                key={idx}
                                                className="text-xs bg-white dark:bg-gray-800 p-2 rounded border border-emerald-100 dark:border-emerald-900/50 shadow-sm"
                                            >
                                                <span className="font-bold text-gray-800 dark:text-gray-200">
                                                    {item.full_name as string}
                                                </span>
                                                <span className="text-gray-500 block">
                                                    {item.email as string}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {(skippedData.length > 0 || importedData.length > 0) && (
                        <div className="mt-4 px-6 flex justify-end">
                            <Button
                                variant="outline"
                                onClick={handleExportResults}
                                className="w-full sm:w-auto bg-white hover:bg-gray-50 border-gray-300 text-gray-700 shadow-sm"
                            >
                                <Download className="w-4 h-4 mr-2" /> Download Laporan Hasil Import (.csv)
                            </Button>
                        </div>
                    )}
                    <div className="mt-6 px-6 pb-6 flex justify-end">
                        <Button variant="ghost" onClick={onClose} disabled={importing}>Tutup</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}